import axios from 'axios';
import {
    SubscriptionEvaluation,
    TestAccount,
    UserDetails,
    UserProfile,
    UserProfileStatistics,
    WorkoutStreak,
} from './users.interface';
import { ApiError } from '../common/errors';

export const USER_SEARCH_ENDPOINT = '/api/users/search';
export const USER_ENDPOINT = '/api/users';
export const TEST_ACCOUNTS_ENDPOINT = `${USER_ENDPOINT}/test-accounts`;

export const getSubscriptionHistoryEvaluation = async (uid: string): Promise<SubscriptionEvaluation> => {
    try {
        const { data } = await axios.get(`${USER_ENDPOINT}/${uid}/subscription-history/evaluation`);

        return data.item as SubscriptionEvaluation;
    } catch (e: any) {
        if (e.response?.status === 404) {
            throw new ApiError('User not found');
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};

export const getUserDetailsByEmailOrUID = async (emailOrUid: string): Promise<UserDetails> => {
    try {
        const { data } = await axios.post(`${USER_SEARCH_ENDPOINT}`, { search: emailOrUid });
        const { item } = data;

        return item as UserDetails;
    } catch (e: any) {
        if (e.response?.status === 404) {
            throw new ApiError('User not found');
        }
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};


export const getUserObjectByUid = async (uid: string): Promise<{ user: UserProfile, stats: UserProfileStatistics}> => {
    try {
        const { data } = await axios.get(`${USER_ENDPOINT}/${uid}`);
        const { item, stats } = data;

        return {
            user: item as UserProfile,
            stats: stats as UserProfileStatistics
        };
    } catch (e: any) {
        if (e.response?.status === 404) {
            throw new ApiError('User not found');
        }
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};

export const updateWorkoutStreak = async (uid: string, workoutStreak: WorkoutStreak): Promise<void> => {
    try {
        await axios.put(`${USER_ENDPOINT}/${uid}/workout-streak`, workoutStreak);
    } catch (e: any) {
        if (e.response?.status === 404) {
            throw new ApiError('User not found');
        }
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};

export const updateUser = async (uid: string, update: Partial<UserDetails>): Promise<void> => {
    try {
        await axios.put(`${USER_ENDPOINT}/${uid}`, update);
    } catch (e: any) {
        if (e.response?.status === 404) {
            throw new ApiError('User not found');
        }
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
}

export const getTestAccounts = async (): Promise<TestAccount[]> => {
    try {
        const { data } = await axios.get(TEST_ACCOUNTS_ENDPOINT);
        return data.items as TestAccount[];
    } catch (e: any) {
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};

export const addTestAccount = async (uid: string): Promise<void> => {
    try {
        await axios.post(TEST_ACCOUNTS_ENDPOINT, { uid });
    } catch (e: any) {
        if (e.response?.status === 404) {
            throw new ApiError('User not found');
        }
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};

export const removeTestAccount = async (uid: string): Promise<void> => {
    try {
        await axios.delete(`${TEST_ACCOUNTS_ENDPOINT}/${uid}`);
    } catch (e: any) {
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};

export const restoreTestAccount = async (uid: string): Promise<void> => {
    try {
        await axios.post(`${TEST_ACCOUNTS_ENDPOINT}/${uid}/restore`);
    } catch (e: any) {
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};

export const cloneUserToTestAccount = async (
    uid: string,
    targetUid: string,
): Promise<void> => {
    try {
        await axios.post(`${USER_ENDPOINT}/${uid}/clone-to-test-account`, {
            targetUid,
        });
    } catch (e: any) {
        if (e.response?.status === 404) {
            throw new ApiError('User not found');
        }
        if (e.response?.status === 400) {
            throw new ApiError(e.response?.data?.message);
        }
        throw new Error(e.response?.data?.message || e.message);
    }
};
